<template>
  <div class="configuration-wrapper translate-configuration-wrapper">
    <Panel>
      <template #header>
        <div class="panel-header-content">
          <h4>{{ t('templateBuilder.panels.translation') }}</h4>
          <SpmButton
            v-if="translatableFieldsCount > 0"
            icon="ai-layout-icon fa-light far fa-language"
            :label="t('templateBuilder.translateAll')"
            class-style="translate-all-button button-ai p-button-sm ai-layout active"
            :disabled="isTranslating"
            @click="openBatchTranslationModal"
          />
        </div>
      </template>

      <template #icons>
        <i
          v-if="isClosable"
          class="far fa-times icons-header-panel"
          @click="handleHideToolbar"
        />
      </template>

      <TabMenu
        v-model:activeIndex="activeIndex"
        :model="languages"
        class="translation-panel--menu"
      >
        <template #item="{ item }">
          <a
            class="p-menuitem-link p-highlight"
            role="presentation"
            @click="handleChangeLanguage(item.label)"
          >
            <span class="p-menuitem-text">
              <img
                :src="`/images/flags/${item.label}.png`"
                alt="language-flag"
              >
            </span>
          </a>
        </template>
      </TabMenu>
      <div>
        <div class="field-group pt-3">
          <div>
            <div class="field-group-content">
              <div
                v-for="group in groupOrder"
                :key="group"
                :ref="el => setGroupComponentRef(el, group)"
                :class="{
                  'border-left-3': groupedTranslations[group].active && groupedTranslations[group].translations.length > 1,
                  'active-group': groupedTranslations[group].active && groupedTranslations[group].translations.length > 1,
                  'is-sync my-1': groupedTranslations[group].syncElement,
                  'is-sync--active': groupedTranslations[group].syncElement && selectedSyncElement && groupedTranslations[group].syncElement.id === selectedSyncElement.elementId
                }"
                class="px-1"
              >
                <div
                  v-if="groupedTranslations[group].syncElement && !selectedSyncElement"
                  class="synchronized-translation__layer"
                >
                  <div class="synchronized-translation__layer__info">
                    <p class="text-center text-white text-md px-2 mx-auto my-2">
                      {{ t('templateBuilder.translationPanel.syncInfo') }}
                    </p>
                    <SpmButton
                      icon="fa-light far fa-language"
                      :label="t('templateBuilder.translationPanel.editSyncElementToTranslate')"
                      class-style="p-button-sm p-button-secondary block mx-auto my-1"
                      @click="handleEditSyncElementTranslation(groupedTranslations[group].syncElement)"
                    />
                  </div>
                </div>
                <div
                  v-for="translation in groupedTranslations[group].translations"
                  :key="`${translation.key}-${translation.language}`"
                  class="field-wrapper"
                >
                  <component
                    :is="getFieldFromType(translation.fieldType, translation.key)"
                    v-if="!translation.hidden"
                    :key="componentKey"
                    :ref="el => setComponentRef(el, translation.key)"
                    v-model="translation.value"
                    :group-key="translation.groupKey"
                    :sync-id="translation.syncElement?.id"
                    :translation="translation"
                    v-bind="getPropsFromType(translation.fieldType, translation)"
                    @update:model-value="(newValue) => handleUpdateModelValue(translation, newValue)"
                    @click="highlightWidgetOnTranslationClicked(translation)"
                    @input="translation.value = $event.target.value"
                    @activate-group="handleActivateGroup"
                    @deactivate-group="handleDeactivateGroup"
                  />
                  <SpmButton
                    v-if="isTranslatableField(translation.fieldType) && !translation.hidden"
                    icon="ai-layout-icon fa-light far fa-language"
                    :label="t('templateBuilder.translate')"
                    class-style="translate-button button-ai p-button-sm block mx-auto my-2 ai-layout active"
                    @click="openTranslationModal(translation)"
                  />
                </div>
              </div>
              <ConfirmDialog group="syncedElements" />
            </div>
          </div>
        </div>
      </div>
    </Panel>

    <!-- Modal de traduction -->
    <TranslationModal
      v-model:visible="showTranslationModal"
      :translation="selectedTranslationForModal"
      :default-language="defaultLanguage"
      :active-language="activeLanguage"
      :is-batch-mode="isBatchMode"
      :translatable-fields="filteredTranslatableFields"
      :batch-mode-progress="batchProgress"
      :batch-mode-translating="isTranslating"
      @translate="handleMultipleTranslation"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, onBeforeMount, onBeforeUnmount,
  Ref,
  ref,
  watch,
  nextTick,
} from 'vue';
import { store } from '@/store';
import TabMenu from 'primevue/tabmenu';
import { useConfirm } from 'primevue/useconfirm';
import {
  addTranslationsToState,
  getTranslationByKeyAndLang,
  hideLeftToolbar,
  replaceTranslationInState,
  TemplateEditorState as state,
  refreshAttributeInDOM,
  setSelectedTranslationId,
  setShowConfigurationPanelCallback, restoreSmartListsOriginalCode,
  getWidgetFromTranslationKey,
  updateTemplatePreviewDisplay,
} from '@/composables/template-editor/TemplateEditor';
import { markTranslationsAsChanged } from '@/composables/template-editor/History';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import LazyWysiwygEditor from '@/components/fields/LazyWysiwygEditor.vue';
import BaseHtmlCodeEditor from '@/components/fields/BaseHtmlCodeEditor.vue';
import BaseImage from '@/components/fields/BaseImage.vue';
import BaseTextarea from '@/components/fields/BaseTextarea.vue';
import BaseLink from '@/components/fields/BaseLink.vue';
import LazyFieldWrapper from '@/components/fields/LazyFieldWrapper.vue';
import LazyBaseInputText from '@/components/fields/LazyBaseInputText.vue';
import LazyBaseTextarea from '@/components/fields/LazyBaseTextarea.vue';
import LazyBaseImage from '@/components/fields/LazyBaseImage.vue';
import LazyBaseLink from '@/components/fields/LazyBaseLink.vue';
import {
  LinkTypeEnum,
  Translation,
  SyncElementWarning,
  SelectedSyncElement,
  WidgetTypeEnum,
} from '@/types';
import variables from '@/components/template-builder/utils/variables-list';
import {
  translateFromGoogle,
  updateIframeTranslation,
  updateIframeTranslations,
} from '@/components/template-builder/utils/translate';
import {
  COUNTDOWN_LABEL_FIELD_TYPE,
  sanitizeCountdownLabel,
} from '@/components/template-builder/utils/countdown-email-i18n';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';
import { getVariablesWithCollectionContext } from '@/composables/template-builder/collectionContext';
import {
  Lang,
  UserState,
} from '@/composables/User';
import { useI18n } from 'vue-i18n';
import Panel from 'primevue/panel';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { refreshSmartProductList } from '@/components/template-builder/callbacks';
import { refreshWhatsappSmartlist } from '@/components/template-builder/setters/whatsapp-smart-product-list';
import {
  getTemplateIframeDocument,
  highlightWidgetOnTranslationClicked,
  isPopupTemplate,
  isRichTextingTemplate,
  isWhatsappTemplate,
  showEditSyncElementWarning,
  decorateActiveItemElementWithStyles,
} from '../utils/helpers';
import { TRANSLATION_MARKUP } from '../utils/constants';
import TranslationModal from '../modals/TranslationModal.vue';

interface SyncElement {
  id: string;
  type: string;
}

interface GroupTranslation {
  translations: Translation[];
  active: boolean;
  syncElement?: SyncElement;
}

interface TranslationRequest {
  sourceLanguage: string;
  targetLanguages: string[];
  translation: Translation;
}

export default defineComponent({
  name: 'TranslationPanel',

  components: {
    SpmButton,
    TabMenu,
    BaseInputText,
    LazyWysiwygEditor,
    BaseHtmlCodeEditor,
    BaseImage,
    BaseTextarea,
    BaseLink,
    Panel,
    TranslationModal,
    LazyFieldWrapper,
    LazyBaseInputText,
    LazyBaseTextarea,
    LazyBaseImage,
    LazyBaseLink,
  },

  props: {
    isClosable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  setup() {
    const { t } = useI18n();
    const componentKey = ref(0);

    // États pour la modal de traduction
    const showTranslationModal = ref(false);
    const selectedTranslationForModal = ref<Translation | null>(null);
    const isBatchMode = ref(false);
    const isTranslating = ref(false);
    const batchProgress = ref(0);

    // Enriched variables with custom properties
    const enrichedVariables = ref(variables.allVariables);

    const defaultLanguage = UserState.activeShop?.langs
      .filter((language: Lang) => language.default)
      .map((language: Lang) => (language.id))[0] ?? 'fr';

    const activeLanguage = ref(defaultLanguage);

    const confirm = useConfirm();

    const translations: Ref<Translation[]> = ref(state.template?.translations.slice() ?? []);
    const translationsForActiveLanguage: Ref<Translation[]> = ref([]);

    const syncElementWarning = computed<SyncElementWarning>(() => store.getters['liveEditor/getSyncElementWarning']);
    const selectedSyncElement = computed<SelectedSyncElement>(() => store.getters['liveEditor/getSelectedSyncElement']);

    const languages = UserState.activeShop?.langs.map((language: Lang) => ({ label: language.id })) ?? [];

    const activeIndex = computed(() => languages.map((item) => item.label).indexOf(activeLanguage.value));

    const groupOrder = computed<string[]>(() => {
      const order: string[] = [];
      translationsForActiveLanguage.value.forEach((translation) => {
        const { syncElement, groupKey } = translation;
        if (syncElement && syncElement.id && !order.includes(syncElement.id)) {
          if (!selectedSyncElement.value || selectedSyncElement.value.elementId === syncElement.id) {
            order.push(syncElement.id);
          }
        } else if (groupKey && !order.includes(groupKey)) {
          if (!selectedSyncElement.value) {
            order.push(groupKey);
          }
        }
      });

      return order;
    });

    // Computed optimisé pour remplacer le watcher coûteux
    const groupedTranslations = computed(() => {
      const grouped: Record<string, GroupTranslation> = {};

      translationsForActiveLanguage.value.forEach((translation) => {
        const { syncElement } = translation;
        if (translation.groupKey) {
          if (syncElement && syncElement.id && !grouped[syncElement.id]) {
            grouped[syncElement.id] = {
              translations: [],
              active: false,
              syncElement,
            };
          } else if (!grouped[translation.groupKey]) {
            grouped[translation.groupKey] = {
              translations: [],
              active: false,
            };
          }
          if (syncElement && syncElement.id) {
            grouped[syncElement.id].translations.push(translation);
          } else {
            grouped[translation.groupKey].translations.push(translation);
          }
        }
      });

      return grouped;
    });

    // Ref pour maintenir l'état actif des groupes (pour handleActivateGroup/handleDeactivateGroup)
    const groupsActiveState = ref<Record<string, boolean>>({});

    // Computed pour obtenir tous les champs traduisibles
    const translatableFields = computed(() => translationsForActiveLanguage.value.filter(
      (translation) => isTranslatableField(translation.fieldType) && !translation.hidden,
    ));

    // Computed pour obtenir les champs traduisibles filtrés selon le contexte des éléments synchronisés
    const filteredTranslatableFields = computed(() => {
      const allTranslatableFields = translationsForActiveLanguage.value.filter(
        (translation) => isTranslatableField(translation.fieldType) && !translation.hidden,
      );

      // Si aucun élément synchronisé n'est sélectionné
      if (!selectedSyncElement.value) {
        // Exclure tous les champs des éléments synchronisés
        return allTranslatableFields.filter(
          (translation) => !translation.syncElement || !translation.syncElement.id,
        );
      }

      // Si un élément synchronisé est en cours d'édition
      // Inclure uniquement les champs de cet élément synchronisé spécifique
      return allTranslatableFields.filter(
        (translation) => translation.syncElement
        && translation.syncElement.id === selectedSyncElement.value.elementId,
      );
    });

    // Computed pour compter les champs traduisibles dans le panel courant (mis à jour pour utiliser le filtre)
    const translatableFieldsCount = computed(() => filteredTranslatableFields.value.length);

    const getFieldFromType = (type: string, key: string) => {
      switch (type) {
        // The rich text editor flattens tables and inline styles of Custom HTML widgets
        case 'html': return getWidgetFromTranslationKey(key) === WidgetTypeEnum.IMPORT_ZIP ? 'BaseHtmlCodeEditor' : 'LazyWysiwygEditor';
        case 'image': return 'LazyBaseImage';
        case 'textarea': return 'LazyBaseTextarea';
        case 'link': return 'LazyBaseLink';
        case 'data-redirect': return 'LazyBaseLink';
        case 'data-selectoptionplaceholder': return 'LazyBaseInputText';
        case 'data-selectoptionlabel': return 'LazyBaseInputText';
        case 'data-spm-text': return 'LazyBaseInputText';
        case 'data-spm-slot-symbol': return 'LazyBaseInputText';
        case 'data-spm-slot-prize': return 'LazyBaseInputText';
        case 'data-rcs-url-description': return 'LazyBaseInputText';
        case 'countdown-label': return 'LazyBaseInputText';
        case undefined: return 'LazyBaseLink'; // hack for pushNotification link, may be risky
        default: {
          if (getWidgetFromTranslationKey(key) === WidgetTypeEnum.TITLE) return 'LazyBaseTextarea';
          // RCS message content should render as textarea
          if (state.template && isRichTextingTemplate(state.template.type)) {
            const iframeDoc = getTemplateIframeDocument();
            if (iframeDoc) {
              const el = iframeDoc.querySelector(`${TRANSLATION_MARKUP}[id='LANG_${key}']`);
              if (el?.closest('.rcs-message__text')) return 'LazyBaseTextarea';
            }
          }
          return 'LazyBaseInputText';
        }
      }
    };

    // Determine maxLength for an RCS translation field by checking its parent element in the iframe DOM
    const getRcsTranslationMaxLength = (translationKey: string): Record<string, any> => {
      if (!state.template || !isRichTextingTemplate(state.template.type)) return {};

      const iframeDoc = getTemplateIframeDocument();
      if (!iframeDoc) return {};

      const translationEl = iframeDoc.querySelector(`${TRANSLATION_MARKUP}[id='LANG_${translationKey}']`);
      if (!translationEl) return {};

      if (translationEl.closest('.rcs-message__title')) {
        return { maxLength: 200 };
      }
      if (translationEl.closest('.rcs-message__text')) {
        return { maxLengthTextarea: 2000 };
      }
      if (translationEl.closest('.rcs-message__cta__item')) {
        return { maxLength: 25 };
      }

      return {};
    };

    const getPropsFromType = (type: string, translation: Translation) => {
      switch (type) {
        case 'text':
          return {
            displayEmojis: true,
            displayVariables: true,
            variablesList: enrichedVariables.value,
            ...getRcsTranslationMaxLength(translation.key),
          };
        case 'link':
          // eslint-disable-next-line no-case-declarations
          let options = [LinkTypeEnum.WEB_ADDRESS, LinkTypeEnum.EMAIL_ADDRESS, LinkTypeEnum.ANCHOR, LinkTypeEnum.NONE];
          // eslint-disable-next-line no-case-declarations
          const params = {
            hideEmailSubject: false,
            hideEmailBody: false,
          };
          if (state.template && isPopupTemplate(state.template.type)) {
            options.splice(options.indexOf(LinkTypeEnum.NONE), 1);
            options.push(LinkTypeEnum.CLOSE_POPIN);
          }
          if (state.template && (isRichTextingTemplate(state.template.type) || isWhatsappTemplate(state.template.type))) {
            options = [LinkTypeEnum.NONE, LinkTypeEnum.WEB_ADDRESS, LinkTypeEnum.EMAIL_ADDRESS, LinkTypeEnum.QUICK_REPLY, LinkTypeEnum.CALL_NUMBER];
            params.hideEmailSubject = true;
            params.hideEmailBody = true;
          }
          return {
            linkTypeOptions: options,
            variablesList: enrichedVariables.value,
            ...params,
          };
        case 'data-redirect':
          return {
            linkTypeOptions: [LinkTypeEnum.WEB_ADDRESS, LinkTypeEnum.DISPLAY_TEMPLATE],
            variablesList: enrichedVariables.value,
          };
        case undefined: // hack for pushNotification link, may be risky
          return {
            linkTypeOptions: [LinkTypeEnum.WEB_ADDRESS, LinkTypeEnum.DISPLAY_TEMPLATE],
            variablesList: enrichedVariables.value,
          };
        default:
          return {
            displayEmojis: true,
            displayVariables: true,
            variablesList: enrichedVariables.value,
            ...getRcsTranslationMaxLength(translation.key),
          };
      }
    };

    const isTranslatableField = (type: string): boolean => {
      switch (type) {
        case 'html': return true;
        case 'image': return false;
        case 'countdown-label': return true;
        case 'textarea': return true;
        case 'link': return false;
        case 'text': return true;
        case 'placeholder': return true;
        case 'data-selectoptionplaceholder': return true;
        case 'data-selectoptionlabel': return true;
        case 'data-spm-text': return true;
        case 'data-spm-slot-symbol': return true;
        case 'data-spm-slot-prize': return true;
        case 'data-rcs-url-description': return true;
        case undefined: return false; // hack for pushNotification link, may be risky
        default: return false;
      }
    };

    const createTranslationsFormLanguage = (language: string) => translations.value
      .filter((translation: Translation) => translation.language === defaultLanguage)
      .map((translation: Translation) => {
        const translationInNewLanguage = { ...translation };
        translationInNewLanguage.language = language;
        return translationInNewLanguage;
      });

    const handleChangeLanguage = (language: string) => {
      translationsForActiveLanguage.value = [];
      activeLanguage.value = language;
      if (state.template) {
        // We restore all smart lists in the template
        restoreSmartListsOriginalCode(null);
        translationsForActiveLanguage.value.push(...translations.value.filter((translation: Translation) => translation.language === activeLanguage.value));
        if (translationsForActiveLanguage.value.length === 0) {
          translationsForActiveLanguage.value = createTranslationsFormLanguage(language);
          // add new translations to component object
          translations.value.push(...translationsForActiveLanguage.value);
          // add new translations in state
          addTranslationsToState(translationsForActiveLanguage.value);
        }
        refreshSmartProductList(null, language);
        // Refresh WhatsApp smartlist with the new language for fillers
        refreshWhatsappSmartlist(language);
        updateIframeTranslations(translationsForActiveLanguage.value);
        refreshAttributeInDOM(language);

        updateTemplatePreviewDisplay();
      }
    };

    handleChangeLanguage(activeLanguage.value);

    // Fonction pour ouvrir la modal de traduction
    const openTranslationModal = (translation: Translation) => {
      selectedTranslationForModal.value = translation;
      isBatchMode.value = false;
      showTranslationModal.value = true;
    };

    // Fonction pour ouvrir la modal de traduction en lot
    const openBatchTranslationModal = () => {
      selectedTranslationForModal.value = null;
      isBatchMode.value = true;
      showTranslationModal.value = true;
    };

    // Fonction pour gérer la traduction en lot de tous les champs
    const handleBatchTranslation = async (request: TranslationRequest) => {
      const { sourceLanguage, targetLanguages } = request;
      const fieldsToTranslate = filteredTranslatableFields.value;

      // Ne planifier que les champs qui ont une valeur source non vide pour la langue source sélectionnée
      // On conserve également la valeur source pour éviter un second lookup et garantir le typage non-null
      const validFields = fieldsToTranslate
        .map((field) => {
          const st = getTranslationByKeyAndLang(field.key, sourceLanguage);
          const val = st?.value?.trim();
          return val ? { field, sourceValue: st!.value } : null;
        })
        .filter((v) => v !== null) as { field: Translation; sourceValue: string }[];

      if (validFields.length === 0) {
        console.warn('Aucun champ traduisible avec une valeur source non vide');
        return;
      }

      isTranslating.value = true;
      batchProgress.value = 0;
      const totalTranslationsPlanned = validFields.length * targetLanguages.length;
      let doneTranslations = 0;
      let totalTranslations = 0;
      let successfulTranslations = 0;
      let failedTranslations = 0;

      try {
        // Traiter chaque champ traduisible
        for (let i = 0; i < validFields.length; i++) {
          const { field, sourceValue } = validFields[i];

          // Traduire vers chaque langue cible
          for (let i1 = 0; i1 < targetLanguages.length; i1++) {
            const targetLang = targetLanguages[i1];
            totalTranslations += 1;

            try {
              // eslint-disable-next-line no-await-in-loop
              let translationResult = await translateFromGoogle(sourceValue, targetLang);
              translationResult = translationResult.replace(/\n/gi, '').replace(/<br\s*\/?>(?=\n|$)/gi, '\n');

              // Mettre à jour la traduction dans l'état
              replaceTranslationInState(field.key, targetLang, translationResult);

              // Si on traduit la langue active, mettre à jour l'objet lié au panel pour forcer la réactivité immédiate
              if (targetLang === activeLanguage.value) {
                field.value = translationResult;
                const updatedTranslation = { ...field, language: targetLang, value: translationResult };
                updateIframeTranslation(updatedTranslation);
                refreshAttributeInDOM(targetLang, field.key);
              }

              successfulTranslations += 1;
            } catch (error) {
              failedTranslations += 1;
              console.error(`✗ Erreur traduction ${field.key} -> ${targetLang}:`, error);
            } finally {
              doneTranslations += 1;
              batchProgress.value = Math.round((doneTranslations / totalTranslationsPlanned) * 100);
            }

            // Petite pause pour éviter de surcharger l'API
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        // Rafraîchir l'interface
        componentKey.value += 1;
        markTranslationsAsChanged();

        // Each translated key was already injected into the iframe DOM via
        // updateIframeTranslation inside the loop. Re-applying all
        // translationsForActiveLanguage here would overwrite the resolved
        // smart-list product clones with template values like {var=product.name}.
      } catch (error) {
        console.error('Erreur lors de la traduction par lot:', error);
      } finally {
        isTranslating.value = false;
        batchProgress.value = 100;
        // Fermer la modale à la fin du batch
        showTranslationModal.value = false;
      }
    };

    // Fonction pour gérer les traductions multiples (champ unique)
    const handleMultipleTranslation = async (request: TranslationRequest) => {
      if (isBatchMode.value) {
        return handleBatchTranslation(request);
      }

      const { sourceLanguage, targetLanguages, translation } = request;

      // Obtenir la valeur à traduire depuis la langue source
      const sourceTranslation = getTranslationByKeyAndLang(translation.key, sourceLanguage);

      if (!sourceTranslation || !sourceTranslation.value.trim()) {
        console.warn('Aucune valeur trouvée pour la traduction source');
        // eslint-disable-next-line consistent-return
        return;
      }

      isTranslating.value = true;

      try {
        // Traduire vers chaque langue cible
        const translationPromises = targetLanguages.map(async (targetLang) => {
          try {
            let translationResult = await translateFromGoogle(sourceTranslation.value, targetLang);
            translationResult = translationResult.replace(/\n/gi, '').replace(/<br\s*\/?>/gi, '\n');

            // Mettre à jour la traduction dans l'état
            replaceTranslationInState(translation.key, targetLang, translationResult);

            // Mettre à jour l'iframe si c'est la langue active
            if (targetLang === activeLanguage.value) {
              const updatedTranslation = { ...translation, language: targetLang, value: translationResult };
              updateIframeTranslation(updatedTranslation);
            }

            return { language: targetLang, success: true, result: translationResult };
          } catch (error) {
            console.error(`Erreur lors de la traduction vers ${targetLang}:`, error);
            return { language: targetLang, success: false, error };
          }
        });

        // Attendre toutes les traductions
        const results = await Promise.all(translationPromises);

        // Rafraîchir l'interface
        componentKey.value += 1;
        markTranslationsAsChanged();

        // The translated key has already been injected into the iframe DOM via
        // updateIframeTranslation above. We deliberately do NOT re-apply all
        // translationsForActiveLanguage here: state values for keys that live inside
        // a rendered smart list still hold the template form (with {var=product.name}
        // etc.), and re-injecting them would overwrite the resolved product clones.

        // Log des résultats
        const successCount = results.filter((r) => r.success).length;
        const failureCount = results.filter((r) => !r.success).length;
      } catch (error) {
        console.error('Erreur lors des traductions multiples:', error);
      } finally {
        isTranslating.value = false;
      }
    };

    const handleUpdateModelValue = async (translation: any, newValue: any) => {
      // Countdown labels ride inside the GIF URL query string — strip characters
      // that would make it ambiguous once the backend injects the text.
      const value = translation.fieldType === COUNTDOWN_LABEL_FIELD_TYPE
        ? sanitizeCountdownLabel(String(newValue ?? ''))
        : newValue;
      replaceTranslationInState(translation.key, translation.language, value);
      updateIframeTranslation(translation);
      refreshAttributeInDOM(translation.language, translation.key);
      markTranslationsAsChanged();

      updateTemplatePreviewDisplay();
    };

    const componentRefs: Ref<Record<string, any>> = ref({});
    const groupComponentRefs: Ref<Record<string, any>> = ref({});
    const highlightTimeout = ref();

    const setComponentRef = (el: any, translationId: string) => {
      if (el) {
        componentRefs.value[translationId] = el;
      }
    };
    const setGroupComponentRef = (el: any, groupKey: string) => {
      if (el) {
        groupComponentRefs.value[groupKey] = el;
      }
    };

    const removeHighlight = () => {
      if (highlightTimeout.value) {
        clearTimeout(highlightTimeout.value);
      }
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const groupKey in groupComponentRefs.value) {
        const componentGroup = groupComponentRefs.value[groupKey];
        componentGroup.classList.remove('spm-input-highlight');
      }
    };
    // TODO: Update callbacks
    const handleEditSyncElementTranslation = (syncElement: SyncElement) => {
      store.dispatch('liveEditor/showSyncElementWarning', {
        elementId: syncElement.id,
        elementType: syncElement.type,
        acceptCallback: () => {
          store.dispatch('liveEditor/prepareStructureConfiguration', selectedSyncElement.value.structureConfiguration);

          decorateActiveItemElementWithStyles(`#${syncElement.id}`);
        },
        rejectCallback: null,
      });
    };

    const stopWatchers: any[] = [];

    stopWatchers.push(watch(() => state.selectedTranslationId, async () => {
      if (state.selectedTranslationId) {
        const component = componentRefs.value[state.selectedTranslationId];

        if (component) {
          // ÉTAPE 1: Désactiver temporairement TOUS les Intersection Observers pour éviter les chargements intempestifs
          const allLazyComponents = Object.values(componentRefs.value).filter((comp) => comp && comp.loadComponent && typeof comp.loadComponent === 'function');

          // Désactiver les observers temporairement
          allLazyComponents.forEach((comp) => {
            if (comp && comp.pauseObserver && typeof comp.pauseObserver === 'function') {
              try {
                comp.pauseObserver();
              } catch (error) {
                console.warn('Error pausing observer:', error);
              }
            }
          });

          // ÉTAPE 2: Forcer le chargement du composant ciblé
          if (component && component.loadComponent && typeof component.loadComponent === 'function') {
            try {
              component.loadComponent();
            } catch (error) {
              console.warn('Error loading component:', error);
            }
          }

          // Attendre le chargement du composant ciblé
          await nextTick();
          await new Promise((resolve) => setTimeout(resolve, 150));

          // ÉTAPE 3: Effectuer le scroll avec position fixe
          if (component && component.focus) {
            try {
              removeHighlight();
              const groupKey = component.$attrs?.['group-key'];
              const syncId = component.$attrs?.['sync-id'];
              const componentGroup = syncId ? groupComponentRefs.value[syncId] : groupComponentRefs.value[groupKey];

              if (componentGroup) {
                // Utiliser scrollIntoView avec behavior instant pour éviter les décalages pendant l'animation
                if (component.$el && 'scrollIntoView' in component.$el) {
                  component.$el.scrollIntoView({ behavior: 'instant', block: 'center' });

                  // Puis réactiver les observers après le scroll
                  setTimeout(() => {
                    allLazyComponents.forEach((comp) => {
                      if (comp && comp.resumeObserver && typeof comp.resumeObserver === 'function') {
                        try {
                          comp.resumeObserver();
                        } catch (error) {
                          console.warn('Error resuming observer:', error);
                        }
                      }
                    });
                  }, 100);
                } else if (component && 'scrollIntoView' in component) {
                  component.scrollIntoView();
                }

                componentGroup.classList.add('spm-input-highlight');
                highlightTimeout.value = setTimeout(() => {
                  if (componentGroup) {
                    componentGroup.classList.remove('spm-input-highlight');
                  }
                  if (component && component.focus) {
                    try {
                      component.focus();
                    } catch (error) {
                      console.warn('Error focusing component:', error);
                    }
                  }
                  highlightTimeout.value = null;
                }, 1200);
                setSelectedTranslationId('');
              } else {
                // Réactiver les observers même en cas d'erreur
                allLazyComponents.forEach((comp) => {
                  if (comp && comp.resumeObserver && typeof comp.resumeObserver === 'function') {
                    try {
                      comp.resumeObserver();
                    } catch (error) {
                      console.warn('Error resuming observer:', error);
                    }
                  }
                });
              }
            } catch (error) {
              console.warn('Error in scroll process:', error);
              // Réactiver les observers même en cas d'erreur
              allLazyComponents.forEach((comp) => {
                if (comp && comp.resumeObserver && typeof comp.resumeObserver === 'function') {
                  try {
                    comp.resumeObserver();
                  } catch (error) {
                    console.warn('Error resuming observer:', error);
                  }
                }
              });
            }
          } else {
            // Réactiver les observers même en cas d'erreur
            allLazyComponents.forEach((comp) => {
              if (comp && comp.resumeObserver && typeof comp.resumeObserver === 'function') {
                try {
                  comp.resumeObserver();
                } catch (error) {
                  console.warn('Error resuming observer:', error);
                }
              }
            });
          }
        }
      }
    }));

    watch(() => syncElementWarning, () => {
      if (syncElementWarning.value) {
        const { elementType, acceptCallback, rejectCallback } = syncElementWarning.value;
        showEditSyncElementWarning(
          confirm,
          t,
          elementType,
          acceptCallback,
          rejectCallback,
        );
      }
    }, { deep: true });

    const handleHideToolbar = () => {
      if (state.showConfigurationPanelCallback) {
        state.showConfigurationPanelCallback();
        setShowConfigurationPanelCallback(null);
      } else {
        hideLeftToolbar();
      }
    };

    const handleActivateGroup = (groupKey: string) => {
      groupsActiveState.value[groupKey] = true;
    };

    const handleDeactivateGroup = (groupKey: string) => {
      groupsActiveState.value[groupKey] = false;
    };

    // Computed qui combine groupedTranslations avec l'état actif
    const groupedTranslationsWithActiveState = computed(() => {
      const result: Record<string, GroupTranslation> = {};
      Object.keys(groupedTranslations.value).forEach((groupKey) => {
        result[groupKey] = {
          ...groupedTranslations.value[groupKey],
          active: groupsActiveState.value[groupKey] || false,
        };
      });
      return result;
    });

    onBeforeMount(async () => {
      languages.sort((a, b) => {
        if (a.label === defaultLanguage) {
          return -1;
        }
        if (b.label === defaultLanguage) {
          return 1;
        }
        return 0;
      });

      // Load enriched variables with custom properties
      let baseVariables = variables.allVariables;

      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      enrichedVariables.value = baseVariables;
    });

    onBeforeUnmount(() => {
      if (highlightTimeout.value) {
        clearTimeout(highlightTimeout.value);
      }
      componentRefs.value = {};
      groupComponentRefs.value = {};
      stopWatchers.forEach((stop) => stop());
    });

    return {
      t,
      languages,
      defaultLanguage,
      activeLanguage,
      activeIndex,
      componentKey,
      groupOrder,
      groupedTranslations: groupedTranslationsWithActiveState,
      selectedSyncElement,
      showTranslationModal,
      selectedTranslationForModal,
      isBatchMode,
      isTranslating,
      batchProgress,
      translatableFieldsCount,
      translatableFields,
      filteredTranslatableFields,
      handleChangeLanguage,
      getFieldFromType,
      getPropsFromType,
      highlightWidgetOnTranslationClicked,
      openTranslationModal,
      openBatchTranslationModal,
      handleMultipleTranslation,
      isTranslatableField,
      handleUpdateModelValue,
      setComponentRef,
      handleHideToolbar,
      handleActivateGroup,
      handleDeactivateGroup,
      setGroupComponentRef,
      handleEditSyncElementTranslation,
    };
  },
});
</script>

<style lang="scss" scoped>
.panel-header-content {
  display: flex;
  align-items: center;
  gap: .75rem;
  // add space so the internal button doesn't collide with Panel icons slot (close X)
  padding-right: 2rem;

  h4 {
    margin: 0;
  }

  .translate-all-button {
    margin-left: auto; // push it to the right side of the header content
  }
}

.icons-header-panel {
  cursor: pointer;
  color: #607D8B;
}

.field-group-content {
  .field-wrapper:not(:last-child) {
    margin-bottom: 1.5rem;
  }
}

.active-group {
  border-color: $brand-color-primary;
  padding-left: 10px !important;
  margin-left: -9px;
}

.is-sync {
  position: relative;
  pointer-events: none;
  min-height: 8rem;
  &--active {
    pointer-events: all;
  }
  .synchronized-translation__layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(4px);
    border-radius: .2rem;
    z-index: 1;
    &__info {
      position: -webkit-sticky;
      position: sticky;
      top: 4rem;
      padding-bottom: 1rem;
      z-index: 2;
      pointer-events: all;
    }
  }
}

// Styles pour l'en-tête du panel avec le bouton "Traduire tout"
.panel-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  h4 {
    margin: 0;
    flex: 1;
  }

  .translate-all-button {
    margin-left: 1rem;
    font-size: 0.875rem;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(148, 200, 64, 0.2);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
}
</style>

<style lang="scss">
.translate-configuration-wrapper .p-panel-header {
  height:53px;
  padding: 0 1rem!important;
}
.translation-panel {
  &--menu {
    position: -webkit-sticky;
    position: sticky;
    top: 53px;
    z-index: 3;
  }

}
</style>
